import { useState, useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist';
import { Tooltip } from 'react-tooltip';

const ChartView = ({
  filteredData,
  selectedVariable,
  selectedMeasure,
  dataStructure,
  chartXAxis,
  chartType,
  isSidebarOpen,
  isRightPanelOpen,
  variableDefinitions
}) => {
  // Add debugging for filteredData
  useEffect(() => {
    console.log('ChartView: filteredData changed:', {
      length: filteredData.length,
      hasData: filteredData.length > 0,
      selectedVariable,
      selectedMeasure,
      sampleData: filteredData.slice(0, 3)
    });
  }, [filteredData, selectedVariable, selectedMeasure]);

  const plotlyChartRef = useRef(null);
  const chartContainerRef = useRef(null);
  const [chartDimensions, setChartDimensions] = useState({ width: 800, height: 500 });
  const [isClearing, setIsClearing] = useState(false);
  const [titleWidth, setTitleWidth] = useState(0);
  
  useEffect(() => {
    if (selectedVariable) {
      // Use a more reliable measurement approach
      const measureTitleWidth = () => {
        // Create a temporary element to measure text
        const tempElement = document.createElement('div');
        tempElement.style.position = 'absolute';
        tempElement.style.visibility = 'hidden';
        tempElement.style.fontSize = '20px';
        tempElement.style.fontWeight = 'bold';
        tempElement.style.fontFamily = 'Calibre-Light, Arial, sans-serif';
        tempElement.style.whiteSpace = 'nowrap';
        tempElement.textContent = selectedVariable;
        
        document.body.appendChild(tempElement);
        const width = tempElement.offsetWidth;
        document.body.removeChild(tempElement);
        
        console.log('Measured title width:', width, 'for title:', selectedVariable);
        setTitleWidth(width);
      };

      // Measure immediately and after a short delay to ensure fonts are loaded
      measureTitleWidth();
      setTimeout(measureTitleWidth, 100);
    }
  }, [selectedVariable]);

  // Debug variableDefinitions and variable mismatch
  useEffect(() => {
    console.log('ChartView: variableDefinitions:', variableDefinitions);
    console.log('ChartView: variableDefinitions keys:', variableDefinitions ? Object.keys(variableDefinitions) : 'undefined');
    console.log('ChartView: selectedVariable:', selectedVariable);
    
    // Debug the exact keys to find mismatches
    if (variableDefinitions && selectedVariable) {
      console.log('ChartView: Exact key match check:', {
        selectedVariable,
        exactMatch: variableDefinitions.hasOwnProperty(selectedVariable),
        allKeys: Object.keys(variableDefinitions),
        firstFewKeys: Object.keys(variableDefinitions).slice(0, 10)
      });
      
      // Try to find partial matches
      const partialMatches = Object.keys(variableDefinitions).filter(key => 
        key.toLowerCase().includes(selectedVariable.toLowerCase()) || 
        selectedVariable.toLowerCase().includes(key.toLowerCase())
      );
      console.log('ChartView: Partial matches found:', partialMatches);
    }
  }, [variableDefinitions, selectedVariable]);

  // Debounce utility function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // Font loading utility
  const ensureFontLoaded = () => {
    return new Promise((resolve) => {
      if (document.fonts && document.fonts.check) {
        if (document.fonts.check('300 16px Calibre-Light')) {
          resolve();
        } else {
          document.fonts.ready.then(() => {
            setTimeout(resolve, 100); // Small delay to ensure font is fully loaded
          });
        }
      } else {
        // Fallback for browsers without font loading API
        setTimeout(resolve, 500);
      }
    });
  };

  // Custom function to format numbers for y-axis ticks
  const formatYAxisTick = (value) => {
    if (value === 0) return '0';
    
    const absValue = Math.abs(value);
    
    if (absValue >= 1000000000) {
      // Billions
      const billions = value / 1000000000;
      if (billions === Math.floor(billions)) {
        return billions + 'B';
      } else {
        return billions.toFixed(1) + 'B';
      }
    } else if (absValue >= 1000000) {
      // Millions
      const millions = value / 1000000;
      if (millions === Math.floor(millions)) {
        return millions + 'M';
      } else {
        return millions.toFixed(1) + 'M';
      }
    } else if (absValue >= 1000) {
      // Thousands
      const thousands = value / 1000;
      if (thousands === Math.floor(thousands)) {
        return thousands + 'k';
      } else {
        return thousands.toFixed(1) + 'k';
      }
    } else {
      // Less than 1000
      if (value === Math.floor(value)) {
        return value.toString();
      } else {
        return value.toFixed(1);
      }
    }
  };

  // Function to generate "nice" tick values
  const generateNiceTicks = (minValue, maxValue, targetTickCount = 6) => {
    if (minValue === maxValue) {
      return [minValue];
    }

    const range = maxValue - minValue;
    const roughStep = range / (targetTickCount - 1);
    
    // Find the order of magnitude
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    
    // Normalize the rough step to get a nice step size
    const normalizedStep = roughStep / magnitude;
    let niceStep;
    
    if (normalizedStep <= 1) {
      niceStep = 1;
    } else if (normalizedStep <= 2) {
      niceStep = 2;
    } else if (normalizedStep <= 5) {
      niceStep = 5;
    } else {
      niceStep = 10;
    }
    
    const stepSize = niceStep * magnitude;
    
    // Find nice min and max values
    const niceMin = Math.floor(minValue / stepSize) * stepSize;
    const niceMax = Math.ceil(maxValue / stepSize) * stepSize;
    
    // Generate tick values
    const ticks = [];
    for (let tick = niceMin; tick <= niceMax + stepSize * 0.1; tick += stepSize) {
      // Round to avoid floating point precision issues
      const roundedTick = Math.round(tick / magnitude) * magnitude;
      ticks.push(roundedTick);
    }
    
    return ticks;
  };

  // Calculate available space more accurately - Updated for full height only
  const calculateDimensions = () => {
    if (!chartContainerRef.current) return { width: 800, height: 500 };

    // Get viewport height for full screen extension
    const viewportHeight = window.innerHeight;
    
    // Get the actual container dimensions for width (respects parent layout)
    const container = chartContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate available width using container (respects sidebars)
    // Calculate available height using viewport (extends to bottom)
    const horizontalPadding = 40; // Total horizontal padding (20px on each side)
    const verticalPadding = 20; // Top padding
    const bottomWatermarkSpace = 120; // Increased space for copyright and logo watermarks to prevent cutoff
    
    // Use container width (respects existing layout) but viewport height
    const topOffset = containerRect.top; // Distance from top of viewport
    const availableHeight = Math.max(viewportHeight - topOffset - bottomWatermarkSpace, 400);
    const availableWidth = Math.max(containerRect.width - horizontalPadding, 300);

    console.log('ChartView: Calculating dimensions (height only extension):', {
      viewportHeight,
      containerRect,
      topOffset,
      availableWidth,
      availableHeight,
      isSidebarOpen,
      isRightPanelOpen
    });

    return {
      width: availableWidth,
      height: availableHeight
    };
  };

  // Handle resize for window and sidebar/panel changes
  useEffect(() => {
    const handleResize = () => {
      const newDimensions = calculateDimensions();
      setChartDimensions(newDimensions);

      // Resize Plotly chart if it exists with safety checks
      if (plotlyChartRef.current && window.Plotly && plotlyChartRef.current._fullLayout) {
        setTimeout(() => {
          // Double-check the ref still exists after timeout
          if (plotlyChartRef.current && plotlyChartRef.current._fullLayout) {
            try {
              Plotly.Plots.resize(plotlyChartRef.current);
            } catch (error) {
              console.warn('ChartView: Plotly resize failed (element may have been unmounted):', error);
            }
          }
        }, 100);
      }

      console.log('ChartView: Resizing chart to:', newDimensions);
    };

    const debouncedResize = debounce(handleResize, 150);
    
    // Initial calculation
    setTimeout(handleResize, 100);
    
    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, [isSidebarOpen, isRightPanelOpen]);

  // Recalculate dimensions when sidebar or panel state changes
  useEffect(() => {
    setTimeout(() => {
      const newDimensions = calculateDimensions();
      setChartDimensions(newDimensions);
      
      if (plotlyChartRef.current && window.Plotly && plotlyChartRef.current._fullLayout) {
        setTimeout(() => {
          // Safety check before resize
          if (plotlyChartRef.current && plotlyChartRef.current._fullLayout) {
            try {
              Plotly.Plots.resize(plotlyChartRef.current);
            } catch (error) {
              console.warn('ChartView: Plotly resize failed during state change:', error);
            }
          }
        }, 200);
      }
    }, 300); // Wait for CSS transitions to complete
  }, [isSidebarOpen, isRightPanelOpen]);

  // Utility function to wrap text for tooltip (using newlines for Plotly)
  const wrapTooltipText = (text, maxLength = 50) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    
    const words = text.split(' ');
    let lines = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + word).length <= maxLength) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);

    return lines.join('\n'); // Use newlines instead of <br> for Plotly annotations
  };
  
  const getDefinition = () => {
    console.log('getDefinition called with:', {
      variableDefinitions,
      selectedVariable,
      variableDefinitionsKeys: variableDefinitions ? Object.keys(variableDefinitions) : 'undefined'
    });
    
    if (!variableDefinitions || !selectedVariable) {
      console.log('getDefinition: Missing variableDefinitions or selectedVariable');
      return 'No definition available';
    }
    
    // Direct lookup first
    let definition = variableDefinitions[selectedVariable];
    console.log('getDefinition: Direct lookup result:', definition);
    
    if (!definition || definition.trim() === '' || definition === selectedMeasure || definition === 'Definition not provided') {
      // Try case-insensitive and trimmed match
      const normalizedSelected = selectedVariable.trim().toLowerCase();
      const matchingKey = Object.keys(variableDefinitions).find(key => 
        key.trim().toLowerCase() === normalizedSelected
      );
      console.log('getDefinition: Fallback lookup:', {
        normalizedSelected,
        matchingKey,
        sampleKeys: Object.keys(variableDefinitions).slice(0, 5)
      });
      
      definition = matchingKey ? variableDefinitions[matchingKey] : null;
    }
    
    // Final fallback
    if (!definition || definition.trim() === '' || definition === selectedMeasure || definition === 'Definition not provided') {
      definition = 'No definition available';
    }
    
    console.log('getDefinition: Final result:', definition);
    return definition;
  };

  // CRITICAL FIX: Clear chart whenever measure changes
  // FIXED: Main chart rendering effect with proper dependency management
  useEffect(() => {
    console.log('ChartView: Main chart effect triggered', {
      hasPlotlyRef: !!plotlyChartRef.current,
      filteredDataLength: filteredData.length,
      selectedVariable,
      selectedMeasure,
      isClearing,
    });

    // Prevent multiple simultaneous operations
    if (isClearing) {
      console.log('ChartView: Skipping operation - already clearing');
      return;
    }

    const shouldShowChart = filteredData.length > 0 && selectedVariable && selectedMeasure;
    
    if (!plotlyChartRef.current) {
      console.log('ChartView: No plotly ref available');
      return;
    }

    if (!shouldShowChart) {
      console.log('ChartView: Should clear chart - no valid data');
      setIsClearing(true);
      
      try {
        if (plotlyChartRef.current._fullLayout) {
          Plotly.purge(plotlyChartRef.current);
          console.log('ChartView: Chart purged successfully');
        }
        plotlyChartRef.current.innerHTML = '';
      } catch (error) {
        console.warn('ChartView: Error clearing chart:', error);
        // Fallback: clear the DOM element
        if (plotlyChartRef.current) {
          plotlyChartRef.current.innerHTML = '';
        }
      }
      
      setIsClearing(false);
      return;
    }

    // Ensure font is loaded before creating chart
    ensureFontLoaded().then(() => {
      // CRITICAL: Always clear before creating new chart to prevent stale data
      setIsClearing(true);
      try {
        if (plotlyChartRef.current._fullLayout) {
          Plotly.purge(plotlyChartRef.current);
        }
        plotlyChartRef.current.innerHTML = '';
        console.log('ChartView: Cleared existing chart before creating new one');
      } catch (error) {
        console.warn('ChartView: Error clearing existing chart:', error);
      }

      // Prepare new chart data
      const prepareChartData = () => {
        if (chartXAxis === 'Year') {
          const countryGroups = {};
          filteredData.forEach(row => {
            if (!countryGroups[row.Country]) {
              countryGroups[row.Country] = {
                x: [],
                y: [],
                name: row.Country
              };
            }
            countryGroups[row.Country].x.push(row.Year);
            countryGroups[row.Country].y.push(row.Value);
          });
          
          const traces = [];
          
          // Get colors for consistency between traces
          const colors = [
            '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
            '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5', '#c49c94', '#f7b6d3', '#c7c7c7', '#dbdb8d', '#9edae5'
          ];

          Object.values(countryGroups).forEach((group, index) => {
            const color = colors[index % colors.length];
            
            // Create hover text for this specific group
            const hoverTexts = group.y.map((value, valueIndex) => {
              // Format the number exactly as it appears in data
              const formattedValue = typeof value === 'number' 
                ? value.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 20, // Allow up to 20 decimal places
                    useGrouping: true
                  })
                : value;
              
              return `<b>${group.name}</b><br>Year: ${group.x[valueIndex]}<br>${selectedMeasure || 'Value'}: ${formattedValue}`;
            });
            
            // For line charts, add a transparent line trace for better hover detection
            if (chartType === 'line') {
              traces.push({
                x: group.x,
                y: group.y,
                mode: 'lines',
                type: 'scatter',
                name: group.name,
                line: {
                  width: 8, // Thick invisible line for hover detection
                  color: 'rgba(0,0,0,0)' // Completely transparent
                },
                text: hoverTexts,
                hovertemplate: '%{text}<extra></extra>',
                showlegend: false, // Hide this helper trace from legend
                visible: true
              });
            }
            
            // Main visible trace (lines + markers or just markers/bars)
            traces.push({
              x: group.x,
              y: group.y,
              mode: chartType === 'line' ? 'lines+markers' : 'markers',
              type: chartType === 'bar' ? 'bar' : 'scatter',
              name: group.name,
              text: hoverTexts,
              hovertemplate: '%{text}<extra></extra>',
              line: chartType === 'line' ? {
                width: 2,
                color: color
              } : undefined,
              marker: {
                size: 8,
                color: color
              },
              showlegend: true
            });
          });
          
          return traces;
        } else {
          // For non-Year x-axis (scatter plots), group by country if multiple countries exist
          const countries = [...new Set(filteredData.map(row => row.Country))];
          
          if (countries.length > 1) {
            // Multiple countries - create separate traces for each
            const colors = [
              '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
              '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5', '#c49c94', '#f7b6d3', '#c7c7c7', '#dbdb8d', '#9edae5'
            ];
            
            return countries.map((country, index) => {
              const countryData = filteredData.filter(row => row.Country === country);
              const xValues = [];
              const yValues = [];
              const hoverTexts = [];
              
              countryData.forEach(row => {
                let xValue, yValue;
                if (chartXAxis === 'Value') {
                  xValue = row.Value;
                } else {
                  xValue = row[chartXAxis];
                }
                yValue = row.Value;
                if (xValue !== undefined && yValue !== undefined) {
                  xValues.push(xValue);
                  yValues.push(yValue);
                  // Format numbers with exact precision
                  const formattedX = typeof xValue === 'number' 
                    ? xValue.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 20,
                        useGrouping: true
                      })
                    : xValue;
                  const formattedY = typeof yValue === 'number' 
                    ? yValue.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 20,
                        useGrouping: true
                      })
                    : yValue;
                  hoverTexts.push(`<b>${row.Country}</b><br>X: ${formattedX}<br>Y: ${formattedY}`);
                }
              });
              
              return {
                x: xValues,
                y: yValues,
                text: hoverTexts,
                mode: 'markers',
                type: chartType === 'bar' ? 'bar' : 'scatter',
                name: country,
                marker: {
                  size: 10,
                  color: colors[index % colors.length],
                  opacity: 0.7
                },
                hovertemplate: '%{text}<extra></extra>',
                showlegend: true
              };
            });
          } else {
            // Single country or no country distinction - single trace
            const xValues = [];
            const yValues = [];
            const hoverTexts = [];
            filteredData.forEach(row => {
              let xValue, yValue;
              if (chartXAxis === 'Value') {
                xValue = row.Value;
              } else {
                xValue = row[chartXAxis];
              }
              yValue = row.Value;
              if (xValue !== undefined && yValue !== undefined) {
                xValues.push(xValue);
                yValues.push(yValue);
                // Format numbers with exact precision
                const formattedX = typeof xValue === 'number' 
                  ? xValue.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 20,
                      useGrouping: true
                    })
                  : xValue;
                const formattedY = typeof yValue === 'number' 
                  ? yValue.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 20,
                      useGrouping: true
                    })
                  : yValue;
                hoverTexts.push(`<b>${row.Country}</b><br>X: ${formattedX}<br>Y: ${formattedY}`);
              }
            });
            return [{
              x: xValues,
              y: yValues,
              text: hoverTexts,
              mode: 'markers',
              type: chartType === 'bar' ? 'bar' : 'scatter',
              name: countries[0] || 'Data',
              marker: {
                size: 10,
                color: 'rgb(51, 153, 255)',
                opacity: 0.7
              },
              hovertemplate: '%{text}<extra></extra>',
              showlegend: countries.length > 0 // Only show legend if we have a meaningful name
            }];
          }
        }
      };

      const plotlyDataArray = prepareChartData();
      console.log('ChartView: Prepared chart data:', plotlyDataArray);

      // Determine if we should show legend based on number of traces with showlegend: true
      const tracesWithLegend = plotlyDataArray.filter(trace => trace.showlegend !== false);
      const shouldShowLegend = tracesWithLegend.length > 1;

      let tickvals = [];
      let ticktext = [];
      if (chartXAxis === 'Year' && filteredData.length > 0) {
        const years = [...new Set(filteredData.map(row => row.Year).filter(y => y !== undefined))].sort((a, b) => a - b);
        tickvals = years;
        ticktext = years.map(year => year.toString());
        console.log('ChartView: X-axis years:', { tickvals, ticktext });
      }

      // Calculate y-axis range and generate nice tick values
      const allYValues = plotlyDataArray.flatMap(trace => trace.y || []);
      const minY = Math.min(...allYValues);
      const maxY = Math.max(...allYValues);
      
      // Generate nice tick values
      const customTickVals = generateNiceTicks(minY, maxY, 6);
      const customTickText = customTickVals.map(val => formatYAxisTick(val));

      const layout = {
        width: chartDimensions.width,
        height: chartDimensions.height,
        title: {
          text: selectedVariable || 'Select a Variable',
          font: {
            size: 20,
            family: 'Calibre-Light, Arial, sans-serif',
            weight: 'bold'
          },
          x: 0.5,
          xanchor: 'center',
          pad: {
            t: 20,
            b: 20
          },
          automargin: true,
          wrap: false
        },
        xaxis: {
          title: {
            text: chartXAxis === 'Year' ? 'Year' : chartXAxis,
            font: {
              size: 18,
              family: 'Calibre-Light, Arial, sans-serif',
              weight: 'bold'
            }
          },
          tickfont: {
            family: 'Calibre-Light, Arial, sans-serif',
            size: 14
          },
          // Prevent abbreviation of x-axis values
          tickformat: ',',
          hoverformat: ',.0f',
          ...(chartXAxis === 'Year' && {
            tickmode: 'array',
            tickvals: tickvals,
            ticktext: ticktext,
            tickangle: 45,
            range: tickvals.length > 0 ? [Math.min(...tickvals) - 0.5, Math.max(...tickvals) + 0.5] : undefined
          })
        },
        yaxis: {
          title: {
            text: selectedMeasure || 'Value',
            font: {
              size: 18,
              family: 'Calibre-Light, Arial, sans-serif',
              weight: 'bold'
            },
            standoff: 30,
            automargin: true
          },
          tickfont: {
            family: 'Calibre-Light, Arial, sans-serif',
            size: 14
          },
          // Use custom tick values and text
          tickmode: 'array',
          tickvals: customTickVals,
          ticktext: customTickText,
          hoverformat: ',.0f', // Keep full numbers in hover tooltips
          tickangle: 0,
          automargin: true
        },
        hovermode: 'closest',
        showlegend: shouldShowLegend,
        legend: shouldShowLegend ? {
          x: 1.02,
          y: 1,
          xanchor: 'left',
          yanchor: 'top',
          bgcolor: 'rgba(255,255,255,0.8)',
          bordercolor: '#E5E7EB',
          borderwidth: 1,
          font: {
            size: 12,
            family: 'Calibre-Light, Arial, sans-serif'
          },
          itemsizing: 'constant',
          itemwidth: 30,
          orientation: 'v'
        } : undefined,
        margin: {
          l: 100,
          r: shouldShowLegend ? 120 : 20,
          t: 60,
          b: 80
        },
        autosize: false,
        font: {
          family: 'Calibre-Light, Arial, sans-serif',
          size: 14
        }
      };

      const config = {
        responsive: false,
        displayModeBar: true,
        displaylogo: false,
        modeBarButtons: [[]], // Hide default buttons
      };

      console.log('ChartView: Rendering Plotly chart with layout:', layout);
      
      // Create the new chart
      Plotly.newPlot(plotlyChartRef.current, plotlyDataArray, layout, config).then(() => {
        console.log('ChartView: Plotly chart rendered successfully');
        setIsClearing(false);
        
        // Force a resize after initial render to ensure proper fitting
        setTimeout(() => {
          if (plotlyChartRef.current && window.Plotly && plotlyChartRef.current._fullLayout) {
            try {
              Plotly.Plots.resize(plotlyChartRef.current);
            } catch (error) {
              console.warn('ChartView: Post-render resize failed:', error);
            }
          }
        }, 100);
      }).catch(err => {
        console.error('ChartView: Error rendering Plotly chart:', err);
        setIsClearing(false);
      });
    });

    // Cleanup function
    return () => {
      // Only purge if we're unmounting, not if we're just updating
      console.log('ChartView: Effect cleanup called');
    };
  }, [filteredData, selectedVariable, selectedMeasure, chartXAxis, chartType, chartDimensions, variableDefinitions]);

  // Handle window resize events for Plotly
  useEffect(() => {
    const handlePlotlyResize = debounce(() => {
      if (plotlyChartRef.current && window.Plotly && plotlyChartRef.current._fullLayout && !isClearing) {
        try {
          Plotly.Plots.resize(plotlyChartRef.current);
          console.log('ChartView: Plotly chart resized via window event');
        } catch (error) {
          console.warn('ChartView: Window resize failed (element may have been unmounted):', error);
        }
      }
    }, 200);

    window.addEventListener('resize', handlePlotlyResize);
    return () => window.removeEventListener('resize', handlePlotlyResize);
  }, [isClearing]);

  const InfoIcon = () => <i className="fas fa-circle-info custom-info-icon" />;

  // Custom download function with watermarks
  const downloadChartWithWatermark = async () => {
    if (!plotlyChartRef.current || !plotlyChartRef.current._fullLayout) return;
    
    try {
      // Ensure font is loaded before downloading
      await ensureFontLoaded();
      
      // Get current layout and data from the chart
      const currentLayout = plotlyChartRef.current.layout;
      const currentData = plotlyChartRef.current.data;
      
      // Create layout with watermarks
      const watermarkedLayout = {
        ...currentLayout,
        images: [
          {
            source: '/logo.png',
            xref: 'paper',
            yref: 'paper',
            x: 0.98,
            y: 0.02,
            sizex: 0.12,
            sizey: 0.12,
            xanchor: 'right',
            yanchor: 'bottom',
            opacity: 0.9,
            layer: 'above'
          }
        ],
        annotations: [
          ...(currentLayout.annotations || []),
          {
            text: '© Health Systems Innovation Lab, Harvard University, 2025',
            xref: 'paper',
            yref: 'paper',
            x: 0.01,
            y: 0.01,
            xanchor: 'left',
            yanchor: 'bottom',
            showarrow: false,
            font: {
              size: 11,
              color: 'rgba(0, 0, 0, 0.9)',
              family: 'Calibre-Light, Arial, sans-serif',
              weight: 'bold'
            },
            bgcolor: 'rgba(255, 255, 255, 0.7)', // Lighter background
            borderwidth: 0, // REMOVED: border
            borderpad: 6
          }
        ],
        // ADDED: Ensure global font is applied to the watermarked version too
        font: {
          family: 'Calibre-Light, Arial, sans-serif',
          size: 14
        }
      };
      
      // Temporarily update with watermarks
      await Plotly.react(plotlyChartRef.current, currentData, watermarkedLayout);
      
      // Download the image
      await Plotly.downloadImage(plotlyChartRef.current, {
        format: 'png',
        filename: `chart_${selectedVariable || 'data'}`,
        height: chartDimensions.height,
        width: chartDimensions.width,
        scale: 2
      });
      
      // Revert to original layout without watermarks
      await Plotly.react(plotlyChartRef.current, currentData, currentLayout);
      
    } catch (error) {
      console.error('Error downloading chart with watermark:', error);
    }
  };

  return (
    <div
      className="chart-view-container"
      ref={chartContainerRef}
      style={{
        width: '100%',
        height: '100vh',
        minHeight: '100vh',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        padding: '5px',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      <div
        className="plotly-chart-container"
        style={{
          flex: '1',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          position: 'relative'
        }}
      >
        {/* Always render the plotly div so ref is always available */}
        <div
          ref={plotlyChartRef}
          className="plotly-chart"
          style={{
            width: `${chartDimensions.width}px`,
            height: `${chartDimensions.height}px`,
            display: (filteredData.length === 0 || !selectedVariable || !selectedMeasure) ? 'none' : 'block'
          }}
        />
        
        {/* Custom download button */}
        {(filteredData.length > 0 && selectedVariable && selectedMeasure) && (
          <button
            onClick={downloadChartWithWatermark}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: '12px',
              color: '#374151',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontFamily: 'Calibre-Light, Arial, sans-serif', // UPDATED: Added Calibre-Light
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(249, 250, 251, 1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
            }}
            title="Download chart as PNG with watermark"
          >
            <i className="fas fa-download" style={{ fontSize: '10px' }}></i>
            PNG
          </button>
        )}
        
        {/* Info icon with React tooltip - positioned to avoid overlap */}
        {(filteredData.length > 0 && selectedVariable && selectedMeasure) && (
          <>
            <div
              data-tooltip-id="chart-variable-tooltip"
              data-tooltip-content={getDefinition()}
              style={{
                position: 'absolute',
                top: '15px',
                left: '50%',
                transform: `translateX(${Math.max(titleWidth / 2 + 30, 60)}px)`, // Increased padding and minimum
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '0',
                width: 'auto',
                height: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '18px',
                color: '#3B82F6',
                zIndex: 1000,
                padding: '4px',
                fontWeight: 'bold',
                fontFamily: 'Calibre-Light, Arial, sans-serif'
              }}
            >
              ⓘ
            </div>
          </>
        )}
                
        {/* Show no-data message when appropriate */}
        {(filteredData.length === 0 || !selectedVariable || !selectedMeasure) && !isClearing && (
          <div className="no-data-message" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280',
            textAlign: 'center',
            padding: '40px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontFamily: 'Calibre-Light, Arial, sans-serif' // UPDATED: Added Calibre-Light
          }}>
            <p style={{ fontSize: '18px', marginBottom: '10px' }}>No data available for current selection.</p>
            <p style={{ fontSize: '14px', color: '#9ca3af' }}>Try adjusting your filters to see data in the chart.</p>
          </div>
        )}
        
        {/* Show loading state while clearing/updating */}
        {isClearing && (
          <div className="loading-message" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280',
            textAlign: 'center',
            padding: '40px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontFamily: 'Calibre-Light, Arial, sans-serif' // UPDATED: Added Calibre-Light
          }}>
            <p style={{ fontSize: '16px' }}>Updating chart...</p>
          </div>
        )}
      </div>
      
      {/* React Tooltip for info icon */}
      <Tooltip
        id="chart-variable-tooltip"
        place="bottom-start"
        style={{ 
          backgroundColor: '#1F2937', 
          color: '#FFFFFF', 
          maxWidth: '400px', 
          zIndex: 9999,
          whiteSpace: 'pre-line',
          wordWrap: 'break-word',
          fontFamily: 'Calibre-Light, Arial, sans-serif' // UPDATED: Added Calibre-Light
        }}
      />
    </div>
  );
};

export default ChartView;